import { patchState, signalStore, type, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { setLoaded, setLoading, withCallState } from '@twist/core';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  CreateOrganizationMemberRoleCommandResult,
  DeleteOrganizationMemberRoleCommandResult,
  Organization,
  OrganizationMemberRole,
  OrganizationPermissions,
  UpdateOrganizationMemberRoleCommandResult
} from '@twist/backbone-api-services/ciam';
import { removeEntity, setAllEntities, setEntity, withEntities } from '@ngrx/signals/entities';
import { CONTEXT_ORGANIZATION_KEY } from '../../index';
import { OrganizationMemberRolesService } from '../services/organization-member-roles.service';

export interface OrganizationMemberRolesStoreState {
  organizationKey: string | undefined;
  organization: Organization | undefined;
}

const initialState: OrganizationMemberRolesStoreState = {
  organizationKey: undefined,
  organization: undefined
};

export const OrganizationMemberRolesStore = signalStore(
  withState<OrganizationMemberRolesStoreState>(initialState),
  withEntities({ entity: type<OrganizationMemberRole>(), collection: 'roles' }),
  withCallState(),
  withComputed(state => {
      return {};
    }
  ),
  withMethods(state => {
    const organizationKey = inject(CONTEXT_ORGANIZATION_KEY);
    const memberManagementService = inject(OrganizationMemberRolesService);
    const _router = inject(Router);

    const initialize = async () => {
      console.log('[OrganizationMemberRolesStore] - Initializing');
      patchState(state, setLoading());

      if (!organizationKey) {
        await _router.navigateByUrl('/errors/404', { skipLocationChange: true });
        return;
      }

      const memberRolesQueryResult = await memberManagementService.getOrganizationMemberRoles(organizationKey);
      if (memberRolesQueryResult.results) {
        patchState(state, setAllEntities(memberRolesQueryResult.results, {
          collection: 'roles',
          selectId: x => x.key
        }));
      }
      patchState(state, setLoaded())
    };

    const addMemberRole = async (name: string,
                                 isDefaultRole: boolean,
                                 permissions: Array<OrganizationPermissions>): Promise<CreateOrganizationMemberRoleCommandResult> => {
      const commandResult = await memberManagementService.createOrganizationMemberRole(
        organizationKey,
        {
          name,
          isDefaultRole,
          permissions
        });

      patchState(state, setEntity(commandResult.result, { collection: 'roles', selectId: x => x.key }));

      return commandResult;
    };

    const updateMemberRole = async (roleKey: string, name: string,
                                    isDefaultRole: boolean,
                                    permissions: Array<OrganizationPermissions>): Promise<UpdateOrganizationMemberRoleCommandResult> => {
      const commandResult = await memberManagementService.updateOrganizationMemberRole(
        organizationKey,
        roleKey,
        {
          name,
          isDefaultRole,
          permissions
        });

      patchState(state, setEntity(commandResult.result, { collection: 'roles', selectId: x => x.key }));

      return commandResult;
    };

    const deleteMemberRole = async (roleKey: string): Promise<DeleteOrganizationMemberRoleCommandResult> => {
      const commandResult = await memberManagementService.deleteOrganizationMemberRole(
        organizationKey,
        roleKey);

      patchState(state, removeEntity(roleKey, { collection: 'roles' }));

      return commandResult;
    };

    return {
      initialize,
      addMemberRole,
      updateMemberRole,
      deleteMemberRole
    };
  }),
  withHooks({
    onInit: store => {
      store.initialize();
    }
  })
);
